/**
 * ViewAllProductsLink - Sidebar footer link to the All Products page.
 *
 * Injected at the bottom of the Products section (see NavigationSection). It is
 * not an app entry, so it lives outside the navigation data model / MenuItem
 * type. Mirrors NavigationItem's styling but omits the left icon slot.
 */
import { Link, useLocation } from 'react-router-dom';
import { ListItem, ListItemButton, ListItemText, Typography, alpha } from '@mui/material';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';
import { getIconComponent } from '../../utils/icons';

const ALL_PRODUCTS_PATH = '/all-products';

interface ViewAllProductsLinkProps {
  onNavigate?: () => void;
}

export function ViewAllProductsLink({ onNavigate }: ViewAllProductsLinkProps) {
  const location = useLocation();
  const isActive = location.pathname === ALL_PRODUCTS_PATH;
  const ChevronRightIcon = getIconComponent('ChevronRight');

  return (
    <ListItem disablePadding>
      <ListItemButton
        component={Link}
        to={ALL_PRODUCTS_PATH}
        onClick={onNavigate}
        disableRipple
        selected={isActive}
        sx={(theme) => ({
          px: 2,
          py: 1,
          minHeight: 40,
          borderRadius: 2,
          mx: 1,
          ...(isActive && {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
          }),
          ...(!isActive && {
            '&:hover': { backgroundColor: theme.palette.action.hover },
          }),
        })}
      >
        <ListItemText
          disableTypography
          primary={
            <Typography
              component="span"
              variant="bodySmall"
              fontWeight={400}
              color={isActive ? 'text.main' : capitalDesignTokens.foundations.colors.gray700}
            >
              View all products
            </Typography>
          }
        />
        <ChevronRightIcon
          aria-label="View all products"
          sx={{
            fontSize: 16,
            flexShrink: 0,
            ml: 1,
            color: isActive ? 'text.main' : capitalDesignTokens.foundations.colors.gray700,
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}
